import DOMPurify from 'dompurify';
import { ISystemLink, ISystemLinkContext, resolveSystemLink } from '@/utils/resolveSystemLink';

const textUrlPattern =
    /(?<![@\w])(?:https?:\/\/[^\s<>"'«»]+|(?:[a-z\d](?:[a-z\d-]*[a-z\d])?\.)+[a-z]{2,}(?::\d+)?(?:[/?#][^\s<>"'«»]*)?|\d{1,3}(?:\.\d{1,3}){3}(?::\d+)?(?:[/?#][^\s<>"'«»]*)?)/gi;

const trimPunctuation = (text: string): string => {
    let result = text.replace(/[.,!?;:]+$/, '');
    for (const [open, close] of [
        ['(', ')'],
        ['[', ']'],
        ['{', '}'],
    ]) {
        while (result.endsWith(close) && result.split(close).length > result.split(open).length) {
            result = result.slice(0, -1).replace(/[.,!?;:]+$/, '');
        }
    }
    return result;
};

export const prepareNotificationText = (text: string, context: ISystemLinkContext) => {
    const fragment = DOMPurify.sanitize(text, {
        RETURN_DOM_FRAGMENT: true,
        ADD_ATTR: ['target'],
        FORBID_ATTR: ['data-notification-link'],
    });
    const walker = document.createTreeWalker(fragment, NodeFilter.SHOW_TEXT);
    const textNodes: Text[] = [];
    while (walker.nextNode()) {
        const node = walker.currentNode as Text;
        if (!node.parentElement?.closest('a, pre, code, style, textarea')) textNodes.push(node);
    }

    // Автоссылки строятся только из текстовых узлов; href и атрибуты исходного HTML не переписываются.
    for (const node of textNodes) {
        const content = node.data;
        const replacement = document.createDocumentFragment();
        let offset = 0;
        for (const match of content.matchAll(textUrlPattern)) {
            const url = trimPunctuation(match[0]);
            if (!url) continue;
            replacement.append(document.createTextNode(content.slice(offset, match.index)));
            const anchor = document.createElement('a');
            anchor.setAttribute('href', /^https?:\/\//i.test(url) ? url : `http://${url}`);
            anchor.textContent = url;
            replacement.append(anchor);
            offset = match.index + url.length;
        }
        if (offset) {
            replacement.append(document.createTextNode(content.slice(offset)));
            node.replaceWith(replacement);
        }
    }

    const links: ISystemLink[] = [];
    for (const anchor of fragment.querySelectorAll('a')) {
        if (anchor.target === '_blank') anchor.rel = 'noopener noreferrer';
        if (anchor.querySelector('img, svg, picture') || anchor.hasAttribute('download')) continue;
        const link = resolveSystemLink(
            anchor.getAttribute('href') ?? '',
            anchor.textContent ?? '',
            context,
            anchor.getAttribute('target') ?? '_blank',
        );
        if (!link) continue;
        const slot = document.createElement('span');
        slot.className = 'notification-text__link';
        slot.dataset.notificationLink = String(links.length);
        links.push(link);
        anchor.replaceWith(slot);
    }
    const container = document.createElement('div');
    container.append(fragment);
    return { html: container.innerHTML, links };
};
